import { computed, Injectable, inject, untracked } from '@angular/core';
import { concat, filter, from, map, merge, type Observable, of, Subject, scan } from 'rxjs';
import { Session } from '../session/session';
import type { BrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import type { OrgId, UserId } from '../shared/ids';
import { keyedStreamResource } from '../shared/keyed-stream-resource';
import { realtimeEvents } from '../shared/realtime-events';
import { resourceSettler } from '../shared/resource-settled';
import {
  canEditContents,
  canManage,
  isMemberOrg,
  Segment,
  type Viewer,
  withSkusAdded,
  withSkusRemoved,
} from './segment';
import { type SegmentId, segmentIdSuffix } from './segment-id';
import {
  type SegmentCreateInput,
  type SegmentEvent,
  SegmentRepository,
} from './segment-repository';

/** The segment list together with the user it was loaded for. */
type SegmentSnapshot = {
  readonly userId: UserId;
  readonly segments: readonly Segment[];
};

/** Whom the list is loaded for: the user and their member orgs, known together. */
type ListKey = {
  readonly userId: UserId;
  readonly orgIds: readonly OrgId[];
};

/** A message on the list stream: a fetch starting or landing, or one change to fold in. */
type ListMessage =
  | { readonly kind: 'fetching' }
  | { readonly kind: 'fetched'; readonly segments: readonly Segment[] }
  | { readonly kind: 'event'; readonly event: SegmentEvent };

/**
 * The list as it accumulates: events that arrive while a fetch is in flight
 * are held and folded in once it lands, so nothing is lost to timing.
 */
type ListFold =
  | { readonly kind: 'pending'; readonly events: readonly SegmentEvent[] }
  | { readonly kind: 'loaded'; readonly segments: readonly Segment[] };

const NO_SEGMENTS: readonly Segment[] = [];
const PENDING: ListFold = { kind: 'pending', events: [] };

/**
 * Segment state derived from the session.
 *
 * The user and their member orgs are the resource's parameter, so the list is
 * keyed by identity by construction: another sign-in, a sign-out or a
 * membership change discards it synchronously and loads afresh, and nothing
 * loads before the member orgs are known. The stream that produces the value
 * is a fetch plus every later change, whether it arrives from realtime or
 * from a mutation made here, folded through one reducer; the realtime
 * channels live and die with the stream. `loaded()` therefore always means
 * "loaded for the current user".
 */
@Injectable({ providedIn: 'root' })
export class SegmentStore {
  private readonly repo = inject(SegmentRepository);
  private readonly session = inject(Session);

  private readonly viewer = computed<Viewer>(() => ({
    userId: this.session.userId(),
    orgIds: this.session.orgIds() ?? [],
  }));

  private readonly localEvents$ = new Subject<SegmentEvent>();

  private readonly listKey = computed<ListKey | undefined>(() => {
    const userId = this.session.userId();
    const orgIds = this.session.orgIds();
    return userId && orgIds ? { userId, orgIds } : undefined;
  });

  private readonly list = keyedStreamResource<SegmentSnapshot, ListKey>({
    params: () => this.listKey(),
    stream: (key) => this.listFor(key.userId),
  });

  readonly settled = resourceSettler(this.list, { after: () => this.session.settled() });

  readonly loaded = computed(() => this.list.hasValue());
  /** Why the list or the Session's member orgs failed to load, null while they load or hold. */
  readonly error = computed<Error | null>(() => this.list.error() ?? this.session.error() ?? null);
  readonly segments = computed<readonly Segment[]>(() =>
    this.list.hasValue() ? this.list.value().segments : NO_SEGMENTS
  );

  readonly mySegments = computed<readonly Segment[]>(() => {
    if (!this.list.hasValue()) return NO_SEGMENTS;
    const { userId, segments } = this.list.value();
    return segments.filter((s) => s.owner === userId);
  });

  readonly orgSharedSegments = computed<readonly Segment[]>(() => {
    const { userId, orgIds } = this.viewer();
    return this.segments().filter(
      (s) => isMemberOrg(s, orgIds) && (s.owner === null || (s.sharedWithOrg && s.owner !== userId))
    );
  });

  readonly visibleSegments = computed<readonly Segment[]>(() => {
    const viewer = this.viewer();
    return this.segments().filter((s) => isVisibleTo(s, viewer));
  });

  readonly curatedSegments = computed<readonly Segment[]>(() =>
    this.visibleSegments().filter((s) => s.provenance === 'curated')
  );

  readonly authoredSegments = computed<readonly Segment[]>(() =>
    this.visibleSegments().filter((s) => s.provenance === 'authored')
  );

  readonly editableSegments = computed<readonly Segment[]>(() => {
    const viewer = this.viewer();
    return this.visibleSegments().filter((s) => canEditContents(s, viewer));
  });

  /** Whether the current viewer may change the segment's pins. */
  canEditContents(segment: Segment | null): boolean {
    return canEditContents(segment, this.viewer());
  }

  /** Whether the current viewer may rename, share or delete the segment. */
  canManage(segment: Segment | null): boolean {
    return canManage(segment, this.viewer());
  }

  byId(id: SegmentId): Segment | null {
    return this.segments().find((s) => s.id === id) ?? null;
  }

  /** The segment a /portal/compare/<slug>-<id6> URL names, matched on its id suffix alone. */
  byUrlSuffix(id6: string): Segment | null {
    return this.segments().find((s) => segmentIdSuffix(s.id) === id6) ?? null;
  }

  /**
   * Refetches the current user's list, and the Session's member orgs first if
   * they failed; resolves once it has landed.
   */
  reload(): Promise<void> {
    if (this.session.error()) this.session.reload();
    this.list.reload();
    return this.settled();
  }

  async create(input: SegmentCreateInput): Promise<Segment> {
    const userId = this.session.userId();
    if (!userId) throw new Error('SegmentStore.create requires a signed-in user');
    const segment = await this.repo.create(userId, input);
    this.localEvents$.next({ kind: 'INSERT', segment });
    return segment;
  }

  async rename(id: SegmentId, alias: string): Promise<Segment> {
    return this.applyUpdate(await this.repo.rename(id, alias));
  }

  async share(id: SegmentId): Promise<Segment> {
    return this.applyUpdate(await this.repo.share(id));
  }

  async unshare(id: SegmentId): Promise<Segment> {
    return this.applyUpdate(await this.repo.unshare(id));
  }

  /** Shows the added pins at once; restores and reconciles the row if the write fails. */
  addSkus(id: SegmentId, eans: readonly EAN[]): Promise<Segment> {
    return this.optimisticPins(
      id,
      (s) => withSkusAdded(s, eans),
      () => this.repo.addSkus(id, eans)
    );
  }

  /** Hides the removed pins at once; restores and reconciles the row if the write fails. */
  removeSkus(id: SegmentId, eans: readonly EAN[]): Promise<Segment> {
    return this.optimisticPins(
      id,
      (s) => withSkusRemoved(s, eans),
      () => this.repo.removeSkus(id, eans)
    );
  }

  async replaceItems(
    id: SegmentId,
    params: { alias?: string; skus: readonly EAN[]; brands?: readonly BrandSlug[] }
  ): Promise<Segment> {
    return this.applyUpdate(await this.repo.replaceItems(id, params));
  }

  async delete(id: SegmentId): Promise<void> {
    await this.repo.delete(id);
    this.localEvents$.next({ kind: 'DELETE', id });
  }

  private applyUpdate(segment: Segment): Segment {
    this.localEvents$.next({ kind: 'UPDATE', segment });
    return segment;
  }

  private async optimisticPins(
    id: SegmentId,
    project: (segment: Segment) => Segment,
    write: () => Promise<Segment>
  ): Promise<Segment> {
    const snapshot = this.byId(id);
    const projected = snapshot ? project(snapshot) : null;
    if (projected) this.applyUpdate(projected);
    let server: Segment;
    try {
      server = await write();
    } catch (writeError) {
      if (snapshot) {
        this.applyUpdate(snapshot);
        void this.reconcile(id);
      }
      throw writeError;
    }
    // An equal server row keeps the projected object, so the current segment changes identity once.
    if (!projected || !sameRow(projected, server)) this.applyUpdate(server);
    return server;
  }

  // A realtime event may have landed between the write and the rollback, so the server row wins.
  private async reconcile(id: SegmentId): Promise<void> {
    try {
      const current = await this.repo.getById(id);
      if (current) this.applyUpdate(current);
    } catch {
      // Best effort: the restored snapshot stays until the next event or fetch.
    }
  }

  private listFor(userId: UserId): Observable<SegmentSnapshot> {
    const fetches$ = concat(
      of<ListMessage>({ kind: 'fetching' }),
      from(this.repo.listForUser(userId)).pipe(
        map((segments): ListMessage => ({ kind: 'fetched', segments }))
      )
    );
    const events$ = merge(this.ownershipEvents(userId), this.localEvents$).pipe(
      map((event): ListMessage => ({ kind: 'event', event }))
    );

    return merge(events$, fetches$).pipe(
      scan((fold, message) => this.fold(fold, message), PENDING),
      filter((fold): fold is Extract<ListFold, { kind: 'loaded' }> => fold.kind === 'loaded'),
      map(({ segments }) => ({ userId, segments }))
    );
  }

  private ownershipEvents(userId: UserId): Observable<SegmentEvent> {
    return realtimeEvents<SegmentEvent>((cb) => this.repo.subscribeOwnership(userId, cb));
  }

  private fold(fold: ListFold, message: ListMessage): ListFold {
    switch (message.kind) {
      case 'fetching':
        return PENDING;
      case 'fetched': {
        const held = fold.kind === 'pending' ? fold.events : [];
        return { kind: 'loaded', segments: held.reduce(this.apply, message.segments) };
      }
      case 'event':
        return fold.kind === 'pending'
          ? { kind: 'pending', events: [...fold.events, message.event] }
          : { kind: 'loaded', segments: this.apply(fold.segments, message.event) };
    }
  }

  private readonly apply = (list: readonly Segment[], event: SegmentEvent): readonly Segment[] => {
    const viewer = untracked(this.viewer);
    switch (event.kind) {
      case 'INSERT':
        if (!isVisibleTo(event.segment, viewer)) return list;
        return list.some((s) => s.id === event.segment.id) ? list : [event.segment, ...list];
      case 'UPDATE':
        if (!isVisibleTo(event.segment, viewer))
          return list.filter((s) => s.id !== event.segment.id);
        return replaceOrPrepend(list, event.segment);
      case 'DELETE':
        return list.filter((s) => s.id !== event.id);
    }
  };
}

function replaceOrPrepend(list: readonly Segment[], updated: Segment): readonly Segment[] {
  const idx = list.findIndex((s) => s.id === updated.id);
  if (idx < 0) return [updated, ...list];
  return list.map((s, i) => (i === idx ? updated : s));
}

function sameRow(a: Segment, b: Segment): boolean {
  return (
    a.alias === b.alias &&
    a.sharedWithOrg === b.sharedWithOrg &&
    a.owner === b.owner &&
    a.org === b.org &&
    a.provenance === b.provenance &&
    Segment.authoredKey(a) === Segment.authoredKey(b)
  );
}

function isVisibleTo(segment: Segment, viewer: Viewer): boolean {
  if (viewer.userId && segment.owner === viewer.userId) return true;
  return isMemberOrg(segment, viewer.orgIds) && (segment.sharedWithOrg || segment.owner === null);
}
